import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '../../stores/authStore';

type ResearchGuardProps = {
  children: React.ReactNode;
};

/**
 * Gates the research terminal. Research access is granted SOLELY by an explicit
 * `researchTier` ('research' | 'research-trusted') — admin role does NOT imply
 * access (the two axes are independent; an admin without a tier sees the locked
 * state, same as any user). This mirrors the API promote gate, which is
 * likewise tier-only.
 *
 * Unlike AdminGuard (which redirects), a no-tier user is NOT bounced away — we
 * render an in-place "access required" panel so the feature is discoverable and
 * the user learns how to obtain access.
 */
export function ResearchGuard({ children }: ResearchGuardProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitializing = useAuthStore((s) => s.isInitializing);
  const researchTier = useAuthStore((s) => s.user?.researchTier);
  const location = useLocation();

  // Wait for the session-refresh attempt before deciding.
  if (isInitializing) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const hasAccess = researchTier === 'research' || researchTier === 'research-trusted';
  if (!hasAccess) return <ResearchLocked />;

  return <>{children}</>;
}

/** Shown to authenticated users who lack a research tier. */
function ResearchLocked() {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md rounded-lg border border-border bg-bg-secondary p-8 text-center">
        <div className="mb-3 text-3xl">🔬</div>
        <h1 className="mb-2 text-lg font-semibold text-text-primary">
          Research access required
        </h1>
        <p className="mb-4 text-sm text-text-muted">
          The research terminal lets you author and paper-trade strategies. It is
          gated behind a research tier on your account.
        </p>
        <div className="rounded border border-border bg-bg-primary p-4 text-left text-sm text-text-muted">
          <p className="mb-2 font-medium text-text-primary">How to get access</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Verify your email address and enable two-factor authentication.</li>
            <li>
              For <span className="text-text-primary">trusted</span> access
              (auto-approved strategy promotion), ask an admin to elevate your tier.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
